import { useState } from 'react'
import styled from 'styled-components'

const SectionWrapper = styled.section`
  width: 100%;
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`

const SectionTitle = styled.h2`
  color: black;
  font-size: 48px;
  font-weight: 500;
  text-align: center;
  margin: 0 0 40px;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`

const FAQContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
`

const FAQItem = styled.div`
  border-top: 1.5px solid #CBCBCB;

  &:last-of-type {
    border-bottom: 1.5px solid #CBCBCB;
  }
`

const FAQQuestion = styled.button`
  width: 100%;
  padding: 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  span {
    color: black;
    font-size: 20px;
    font-weight: 500;
    flex: 1;
    padding-right: 20px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`

const FAQToggle = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: #333;
    transition: transform 0.3s;
  }

  &::before {
    width: 14px;
    height: 2px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    width: 2px;
    height: 14px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) ${props => props.isOpen ? 'rotate(90deg)' : 'rotate(0)'};
  }
`

const FAQAnswer = styled.div`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-out;

  p {
    color: #545454;
    font-size: 18px;
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
    padding: 0 0 20px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`

const BottomSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-top: 60px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const BottomTitle = styled.h3`
  color: black;
  font-size: 20px;
  font-weight: 500;
  margin: 0;
`

const BottomText = styled.p`
  color: black;
  font-size: 20px;
  font-weight: 400;
  line-height: 1.6;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const faqData = [
  {
    question: 'What is an AI interior designer?',
    answer: 'An AI interior designer uses artificial intelligence to help you design real rooms by generating layouts, styles, colors, and décor based on your space and preferences. Unlike inspiration tools, an AI interior designer works with your actual room to create realistic, usable designs.'
  },
  {
    question: 'Can I upload a photo of my own room?',
    answer: "Yes. You can upload a photo of your real room to design with AI. DecorMatters' AI interior design tools analyze your space to generate layouts, styles, furniture, colors, and materials that fit your actual room dimensions and structure."
  },
  {
    question: "What's the difference between AI Designer, AI Edit, and AI Refine?",
    answer: 'AI Designer creates complete room designs from scratch or from your uploaded room. AI Edit lets you change specific items or areas—such as furniture, décor, colors, or materials—without redesigning the whole space. AI Refine improves realism, lighting, layout balance, and overall polish to elevate an existing design.'
  },
  {
    question: 'Can I choose specific styles, colors, and materials?',
    answer: 'Yes. You can control interior styles, color palettes, materials, finishes, and mood. The AI interior designer follows your selections to create room designs that match your aesthetic preferences and design goals.'
  },
  {
    question: 'Does this work for renters and small spaces?',
    answer: 'Absolutely. DecorMatters is built for renters, apartments, and small spaces. You can experiment with layouts, furniture, colors, and décor without making permanent changes, making it ideal for temporary or compact homes.'
  },
  {
    question: 'Can I use AI interior design on web, iOS, and Android?',
    answer: "Yes. DecorMatters' AI interior design app is available on web, iOS, and Android, so you can design, edit, and explore room ideas from any device."
  },
  {
    question: 'Are the designs realistic or just AI concepts?',
    answer: "The designs are realistic and made for real rooms. DecorMatters' AI focuses on practical layouts, accurate proportions, and believable lighting so designs can be used as true visual planning tools—not just abstract concepts."
  },
  {
    question: 'Can I save, edit, and revisit my designs later?',
    answer: 'Yes. You can save your AI room designs, edit them over time, compare versions, and revisit your projects whenever you want. Your designs remain accessible across devices.'
  },
  {
    question: 'Do I need interior design experience to use this?',
    answer: 'No. DecorMatters is designed for beginners and experienced designers alike. The AI interior designer guides the process, making room design accessible even if you have no interior design background.'
  }
]

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null)

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <SectionWrapper>
      <SectionTitle>AI Interior Design FAQs</SectionTitle>

      <FAQContainer>
        {faqData.map((item, index) => (
          <FAQItem key={index}>
            <FAQQuestion onClick={() => toggleFAQ(index)}>
              <span>{item.question}</span>
              <FAQToggle isOpen={openIndex === index} />
            </FAQQuestion>
            <FAQAnswer isOpen={openIndex === index}>
              <p>{item.answer}</p>
            </FAQAnswer>
          </FAQItem>
        ))}
      </FAQContainer>

      <BottomSection>
        <BottomTitle>The AI Interior Design App Built for Real Homes</BottomTitle>
        <BottomText>
          DecorMatters is an AI interior design app built for real rooms, real layouts, and real people. With powerful AI tools, you can design your room, explore interior design ideas, and create realistic home designs tailored to your space. From living rooms and bedrooms to apartments and small spaces, DecorMatters helps homeowners, renters, and decorators visualize styles, colors, materials, and layouts before making decisions. Available on iOS, Android, and web, DecorMatters makes AI interior design practical, creative, and accessible for modern homes.
        </BottomText>
      </BottomSection>
    </SectionWrapper>
  )
}

export default FAQSection
